import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import Visibility from "@mui/icons-material/Visibility";
import { Tooltip } from "@mui/material";
import { GridActionsCellItem, GridActionsCellItemProps } from "@mui/x-data-grid-premium";
import { TenantCatalogChemicalFragment } from "generated-graphql/graphql";
import { ReactElement } from "react";

/**
 * Row actions for the chemical catalog grid, for a `type: "actions"` column's
 * getActions. Write access is per-chemical (shared and partner-tenant
 * chemicals may be readable but not writable), so the controls follow the
 * row's canWrite/canDelete capabilities rather than a role permission.
 *
 * These must be `GridActionsCellItem` elements: the grid's actions cell
 * clones what getActions returns to inject its focus-management props
 * (tabIndex, touch-ripple refs), and a custom component would swallow them.
 * Delete stays enabled with `aria-disabled` when the row can't be deleted —
 * `disabled` would set pointer-events: none, hiding the no-permission
 * tooltip — so its onClick guards on canDelete.
 */
export function buildChemicalRowActions({
  row,
  onOpen,
  onDelete,
}: {
  row: TenantCatalogChemicalFragment;
  onOpen: (row: TenantCatalogChemicalFragment) => void;
  onDelete: (row: TenantCatalogChemicalFragment) => void;
}): ReactElement<GridActionsCellItemProps>[] {
  const openAction = row.canWrite ? (
    <GridActionsCellItem
      key="edit"
      label="Edit"
      icon={
        <Tooltip title="Edit" describeChild>
          <Edit />
        </Tooltip>
      }
      onClick={() => onOpen(row)}
    />
  ) : (
    <GridActionsCellItem
      key="view"
      label="View"
      icon={
        <Tooltip title="View" describeChild>
          <Visibility />
        </Tooltip>
      }
      onClick={() => onOpen(row)}
    />
  );

  const deleteAction = (
    <GridActionsCellItem
      key="delete"
      label="Delete Chemical"
      icon={
        <Tooltip
          title={row.canDelete ? "Delete" : "You do not have permission to delete this chemical"}
          describeChild
        >
          <Delete color={row.canDelete ? undefined : "disabled"} />
        </Tooltip>
      }
      aria-disabled={row.canDelete ? undefined : true}
      onClick={() => {
        if (row.canDelete) {
          onDelete(row);
        }
      }}
    />
  );

  return [openAction, deleteAction];
}
